// src/pages/GeofencingDashboard.tsx - ULTRA-COMPACT MOBILE VERSION
import React, { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Stack,
  Divider, InputAdornment, Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as DownloadIcon,
  ContentCopy as CopyIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';

import SearchIcon from '@mui/icons-material/Search';


import api from '../../services/api';
import GeofenceZoneDialog from '../../components/GeofenceZoneDialog';
import BulkZoneDialog from '../../components/BulkZoneDialog';
import ZoneTemplatesDialog from '../../components/ZoneTemplatesDialog';
import GeofenceAnalyticsPanel from '../../components/GeofenceAnalyticsPanel';
import ViolationPredictionWidget from '../../components/ViolationPredictionWidget';

import { formatDateTime } from '../../utils/dateUtils';

interface GeofenceZone {
  _id: string;
  name: string;
  type: 'circular' | 'polygon';
  isActive: boolean;
  rules: {
    alertOnEntry: boolean;
    alertOnExit: boolean;
    alertOnBreach: boolean;
    speedLimit?: number;
  };
  activeSchedule?: {
    enabled: boolean;
    days: number[];
    startTime: string;
    endTime: string;
  };
  overlapsDetected?: any[];
}

interface VehicleGeofenceStatus {
  _id: string;
  plateNumber: string;
  make: string;
  model: string;
  geofencingEnabled: boolean;
  zoneCount: number;
  currentStatus?: {
    isInside: boolean;
    activeZones: Array<{ zoneName: string }>;
  };
}

// ─── Violations Tab ───────────────────────────────────────────────────────────
const ViolationsTab: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const [violations, setViolations] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'acknowledged'>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [bulkSelected, setBulkSelected] = useState<string[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchViolations = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        limit: rowsPerPage,
        skip: page * rowsPerPage,
      };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.acknowledged = statusFilter === 'acknowledged' ? 'true' : 'false';
      if (typeFilter !== 'all') params.violationType = typeFilter;

      const res = await api.get('/geofencing/violations', { params });
      setViolations(res.data.data || []);
      setTotal(res.data.pagination?.total || res.data.data?.length || 0);
    } catch (err) {
      console.error('Error fetching violations:', err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, typeFilter, page, rowsPerPage]);

  useEffect(() => { fetchViolations(); }, [fetchViolations]);
  useEffect(() => { setPage(0); }, [search, statusFilter, typeFilter]);

  const acknowledge = async (violationId: string) => {
    await api.put(`/geofencing/violations/${violationId}/acknowledge`, {});
    fetchViolations();
  };

  const bulkAcknowledge = async () => {
    setBulkLoading(true);
    try {
      await Promise.all(
        bulkSelected.map(id => api.put(`/geofencing/violations/${id}/acknowledge`, {}))
      );
      setBulkSelected([]);
      fetchViolations();
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    setBulkSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const VIOLATION_TYPES = ['all', 'unauthorized_entry', 'unauthorized_exit', 'speed_violation', 'zone_speed_violation', 'global_speed_violation'];

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const surface = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';

  return (
    <Box>
      {/* ── Header ── */}
      <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'stretch' : 'center'} spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">Violations</Typography>
        <Stack direction="row" spacing={1}>
          {bulkSelected.length > 0 && (
            <Button size="small" variant="contained" color="success" onClick={bulkAcknowledge} disabled={bulkLoading}>
              {bulkLoading ? 'Processing...' : `Acknowledge ${bulkSelected.length} selected`}
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => window.open(`${process.env.REACT_APP_API_URL}/geofencing/violations/export?format=csv`, '_blank')}>
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {/* ── Filters ── */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${border}`, background: surface, mb: 2 }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'stretch' : 'center'}>
          <TextField
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search plate or zone…"
            size="small"
            sx={{ minWidth: isMobile ? '100%' : 260 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: subtext }} /></InputAdornment> }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {['all', 'pending', 'acknowledged'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s as any)}
                style={{ padding: '4px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', background: statusFilter === s ? '#f44336' : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: statusFilter === s ? '#fff' : subtext }}>
                {s}
              </button>
            ))}
          </Stack>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              {VIOLATION_TYPES.map(t => (
                <MenuItem key={t} value={t}>{t === 'all' ? 'All Types' : t.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* ── List ── */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${border}`, background: surface, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">Loading...</Typography></Box>
        ) : violations.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography fontWeight="bold">No violations found</Typography>
            <Typography variant="body2" color="text.secondary">Try adjusting your filters</Typography>
          </Box>
        ) : (
          violations.map((v, i) => {
            const isSelected = bulkSelected.includes(v._id);
            const isPending = !v.acknowledgedBy;
            return (
              <Box key={v._id} sx={{ px: 2.5, py: 1.5, borderBottom: i < violations.length - 1 ? `1px solid ${border}` : 'none', bgcolor: isSelected ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)') : 'transparent', display: 'flex', alignItems: 'center', gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                <Checkbox size="small" checked={isSelected} onChange={() => toggleSelect(v._id)} sx={{ flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography fontWeight="bold" fontSize="0.85rem">{v.plateNumber}</Typography>
                    <Box sx={{ px: 1, py: 0.3, borderRadius: 1.5, bgcolor: isPending ? 'rgba(248,113,113,0.12)' : 'rgba(52,211,153,0.12)' }}>
                      <Typography fontSize="0.68rem" fontWeight={800} color={isPending ? '#f87171' : '#34d399'} sx={{ textTransform: 'uppercase' }}>
                        {v.violationType?.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">Zone: <strong>{v.zoneName}</strong></Typography>
                  {v.speed > 0 && <Typography variant="caption" color="text.secondary" display="block">Speed: {Math.round(v.speed)} km/h</Typography>}
                  <Typography variant="caption" color="text.secondary" display="block">{formatDateTime(v.timestamp)}</Typography>
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                  {isPending ? (
                    <Button size="small" variant="outlined" color="success" onClick={() => acknowledge(v._id)} sx={{ fontSize: '0.72rem', textTransform: 'none', fontWeight: 700 }}>
                      Acknowledge
                    </Button>
                  ) : (
                    <Typography fontSize="0.72rem" color="text.secondary">✓ Acknowledged</Typography>
                  )}
                </Box>
              </Box>
            );
          })
        )}

        {/* ── Pagination ── */}
        <Divider sx={{ borderColor: border }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Showing {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, total)} of {total}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Select size="small" value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }} sx={{ fontSize: '0.75rem' }}>
              {[10, 20, 50].map(n => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
            </Select>
            <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Typography variant="caption">Page {page + 1} of {Math.ceil(total / rowsPerPage) || 1}</Typography>
            <Button size="small" disabled={(page + 1) * rowsPerPage >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

const GeofencingDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [vehicles, setVehicles] = useState<VehicleGeofenceStatus[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleGeofenceStatus | null>(null);
  const [zones, setZones] = useState<GeofenceZone[]>([]);
  const [violations, setViolations] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [vehicleSearch, setVehicleSearch] = useState('');
const [zoneSearch, setZoneSearch] = useState('');

  // Dialog states
  const [openZoneDialog, setOpenZoneDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [openTemplatesDialog, setOpenTemplatesDialog] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [multiVehicleMode, setMultiVehicleMode] = useState(false);
  const [editingZone, setEditingZone] = useState<GeofenceZone | null>(null);

  useEffect(() => {
    fetchVehicles();
    fetchViolations();
    fetchStatistics();
    fetchAnalytics();
  }, []);

  useEffect(() => {
    if (selectedVehicle) {
      fetchZones(selectedVehicle._id);
      checkPredictions(selectedVehicle._id);
    }
  }, [selectedVehicle]);

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/geofencing/vehicles');
      setVehicles(response.data.data);
      if (response.data.data.length > 0) {
        setSelectedVehicle(response.data.data[0]);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async (vehicleId: string) => {
    try {
      console.log('🔍 Fetching zones for vehicle:', vehicleId);
      const response = await api.get(`/geofencing/vehicles/${vehicleId}/zones`);
      const zonesData = response.data.data?.zones || response.data.data || [];

      // ✅ ADD: More detailed logging
      console.log('✅ Zones received:', {
        count: zonesData.length,
        zones: zonesData.map((z: any) => ({
          id: z._id,
          name: z.name,
          type: z.type,
          center: z.center,
          radius: z.radius,
          isActive: z.isActive
        }))
      });


      setZones(zonesData);
    } catch (error) {
      console.error('❌ Error fetching zones:', error);
    }
  };

  const fetchViolations = async () => {
    try {
      const response = await api.get('/geofencing/violations?acknowledged=false&limit=10');
      setViolations(response.data.data);
    } catch (error) {
      console.error('Error fetching violations:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/geofencing/statistics');
      setStatistics(response.data.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/geofencing/analytics');
      setAnalytics(response.data.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const [predictions, setPredictions] = useState<any>(null);
  const checkPredictions = async (vehicleId: string) => {
    try {
      const response = await api.get(`/geofencing/vehicles/${vehicleId}/predict`);
      setPredictions(response.data.prediction);
    } catch (error) {
      console.error('Error checking predictions:', error);
    }
  };

  const toggleGeofencing = async (vehicleId: string, enabled: boolean) => {
    try {
      await api.put(`/geofencing/vehicles/${vehicleId}/toggle`, { enabled });
      fetchVehicles();
    } catch (error) {
      console.error('Error toggling geofencing:', error);
    }
  };

  const deleteZone = async (zoneId: string) => {
    if (!selectedVehicle) {
      console.error('❌ No selected vehicle');
      return;
    }

    if (!zoneId || zoneId === 'undefined') {
      console.error('❌ Invalid zone ID:', zoneId);
      alert('Cannot delete zone: Invalid zone ID');
      return;
    }

    console.log('🗑️ Deleting zone:', zoneId, 'from vehicle:', selectedVehicle._id);

    if (!window.confirm('Are you sure you want to delete this zone?')) return;

    try {
      const response = await api.delete(
        `/geofencing/vehicles/${selectedVehicle._id}/zones/${zoneId}`
      );
      console.log('✅ Zone deleted:', response.data);

      fetchZones(selectedVehicle._id);
      fetchStatistics();
      alert('Zone deleted successfully!');
    } catch (error: any) {
      console.error('❌ Delete error:', error.response?.data || error);
      alert(`Failed to delete: ${error.response?.data?.message || error.message}`);
    }
  };

  const duplicateZone = async (zone: GeofenceZone) => {
    if (!selectedVehicle) return;

    try {
      const newZone = {
        ...zone,
        name: `${zone.name} (Copy)`,
        _id: undefined
      };

      await api.post(`/geofencing/vehicles/${selectedVehicle._id}/zones`, newZone);
      fetchZones(selectedVehicle._id);
    } catch (error) {
      console.error('Error duplicating zone:', error);
    }
  };

  const acknowledgeViolation = async (vehicleId: string, violationId: string) => {
    try {
      await api.put(`/geofencing/violations/${violationId}/acknowledge`, {});
      fetchViolations();
    } catch (error) {
      console.error('Error acknowledging violation:', error);
    }
  };

  const exportViolations = async (format: 'csv' | 'json') => {
    try {
      if (format === 'csv') {
        window.open(`${process.env.REACT_APP_API_URL}/api/geofencing/violations/export?format=csv`, '_blank');
      } else {
        const response = await api.get('/geofencing/violations/export?format=json');
        const dataStr = JSON.stringify(response.data.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `violations-${Date.now()}.json`;
        link.click();
      }
    } catch (error) {
      console.error('Error exporting violations:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: 0,
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden',
      bgcolor: 'background.default',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <Box sx={{ mb: 2, width: '100%' }}>
        <Typography
          variant={isMobile ? 'h6' : 'h4'}
          sx={{
            fontWeight: 'bold',
            mb: 1,
            width: '100%'
          }}
        >
          {isMobile ? 'Geofencing' : 'Geofencing Management'}
        </Typography>

        {/* Action buttons - Horizontal scroll on mobile */}
        {isMobile ? (
          <Box sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 1,
            width: '100%',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none'
          }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpenTemplatesDialog(true)}
              sx={{ minWidth: 'fit-content', px: 1.5, whiteSpace: 'nowrap' }}
            >
              Templates
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpenBulkDialog(true)}
              sx={{ minWidth: 'fit-content', px: 1.5, whiteSpace: 'nowrap' }}
            >
              Bulk
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpenExportDialog(true)}
              sx={{ minWidth: 'fit-content', px: 1.5, whiteSpace: 'nowrap' }}
            >
              Export
            </Button>
          </Box>
        ) : (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => setOpenTemplatesDialog(true)}
            >
              Templates
            </Button>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => setOpenBulkDialog(true)}
            >
              Bulk Manage
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => setOpenExportDialog(true)}
            >
              Export
            </Button>
          </Stack>
        )}
      </Box>

      {/* Tabs - FIXED: Prevent horizontal scroll */}
      <Box sx={{ width: '100%', overflowX: 'hidden', mb: 1.5 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{
            width: '100%',
            // ← FIXED: Ensure tabs fit properly
            '& .MuiTabs-flexContainer': {
              gap: isMobile ? 0.5 : 1,
            },
            '& .MuiTab-root': {
              minWidth: isMobile ? 70 : 'auto',
              px: isMobile ? 0.5 : 2,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              textTransform: 'none',
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Zones" />
          <Tab label="Violations" />
          <Tab
            label="Analytics"
            icon={<AnalyticsIcon sx={{ fontSize: isMobile ? 16 : 20 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab 0: Overview */}
      {activeTab === 0 && (
        <>
          {/* Statistics - 2x2 Grid on Mobile */}
          {statistics && (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{ bgcolor: 'background.paper' }}>  {/* ✅ ADD THIS */}
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {isMobile ? 'Vehicles' : 'Total Vehicles'}
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">
                      {statistics.totalVehicles}
                    </Typography>
                    {isMobile ? (
                      <Typography variant="caption" color="primary">
                        {statistics.vehiclesWithGeofencing} active
                      </Typography>
                    ) : (
                      <Chip
                        size="small"
                        label={`${statistics.vehiclesWithGeofencing} with geofencing`}
                        color="primary"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{ bgcolor: 'background.paper' }}>  {/* ✅ ADD THIS */}
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {isMobile ? 'Zones' : 'Active Zones'}
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" color="primary">
                      {statistics.activeZones}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {statistics.totalZones} total
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{ bgcolor: 'background.paper' }}>  {/* ✅ ADD THIS */}
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Violations
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" color="error">
                      {statistics.violations.total}
                    </Typography>
                    {isMobile ? (
                      <Typography variant="caption" color="warning.main">
                        {statistics.violations.unacknowledged} pending
                      </Typography>
                    ) : (
                      <Chip
                        size="small"
                        label={`${statistics.violations.unacknowledged} pending`}
                        color="warning"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{ bgcolor: 'background.paper' }}>  {/* ✅ ADD THIS */}
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Compliance
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" color="success.main">
                      {statistics.violations.total > 0
                        ? Math.round((1 - statistics.violations.unacknowledged / statistics.violations.total) * 100)
                        : 100}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Prediction Widget - Desktop Only */}
          {!isMobile && selectedVehicle && predictions?.hasPrediction && (
            <ViolationPredictionWidget prediction={predictions} vehicle={selectedVehicle} />
          )}

          <Grid container spacing={isMobile ? 1.5 : 3}>
            {/* Fleet Vehicles */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Fleet Vehicles
                  </Typography>
                  {/* Vehicle search */}
                  <TextField
                    size="small"
                    placeholder="Search plate..."
                    fullWidth
                    sx={{ mb: 1.5 }}
                    onChange={e => setVehicleSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  />
                  <List sx={{ p: 0 }}>
                    {vehicles
                      .filter(v => !vehicleSearch || v.plateNumber.toLowerCase().includes(vehicleSearch.toLowerCase()) || `${v.make} ${v.model}`.toLowerCase().includes(vehicleSearch.toLowerCase()))
                      .map((vehicle) => (
                        <ListItem
                          key={vehicle._id}
                          button
                          selected={selectedVehicle?._id === vehicle._id}
                          onClick={() => setSelectedVehicle(vehicle)}
                          sx={{
                            border: 1,
                            borderColor: selectedVehicle?._id === vehicle._id ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            mb: 1,
                            p: isMobile ? 1 : 1.5,
                            bgcolor: selectedVehicle?._id === vehicle._id ? 'action.selected' : 'transparent'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight="bold">
                                  {vehicle.plateNumber}
                                </Typography>
                                {vehicle.geofencingEnabled ? (
                                  <CheckCircleIcon color="success" sx={{ fontSize: isMobile ? 16 : 20 }} />
                                ) : (
                                  <WarningIcon color="warning" sx={{ fontSize: isMobile ? 16 : 20 }} />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="textSecondary">
                                  {vehicle.make} {vehicle.model}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    size="small"
                                    label={`${vehicle.zoneCount} zones`}
                                    sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                    color={vehicle.zoneCount > 0 ? 'primary' : 'default'}
                                  />
                                  {vehicle.currentStatus?.isInside && (
                                    <Chip
                                      size="small"
                                      label="Inside Zone"
                                      color="success"
                                      sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Violations */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Recent Violations
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {violations.length === 0 ? (
                      <Typography variant="caption" color="textSecondary" align="center" sx={{ display: 'block', py: 2 }}>
                        No unacknowledged violations
                      </Typography>
                    ) : (
                      violations.slice(0, 5).map((violation) => (
                        <ListItem
                          key={violation._id}
                          sx={{
                            border: 1,
                            borderColor: 'error.light',
                            borderRadius: 1,
                            mb: 1,
                            p: isMobile ? 1 : 1.5,
                          }}
                        >
                          <ListItemText
                            primary={
                              <Stack spacing={0.5}>
                                <Typography variant={isMobile ? 'caption' : 'body2'} fontWeight="bold">
                                  {violation.plateNumber}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={violation.violationType.replace(/_/g, ' ')}
                                  color="error"
                                  sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                />
                              </Stack>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" display="block">
                                  Zone: {violation.zoneName}
                                </Typography>
                                <Typography variant="caption" display="block" color="textSecondary">
                                  {formatDateTime(violation.timestamp)}
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() =>
                                    acknowledgeViolation(
                                      violation.vehicleId,
                                      violation._id
                                    )
                                  }
                                  sx={{ mt: 0.5, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                  fullWidth={isMobile}
                                >
                                  Acknowledge
                                </Button>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Tab 1: Zones Management */}
      {activeTab === 1 && selectedVehicle && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold" sx={{ mb: isMobile ? 1 : 0 }}>
                    Zones for {selectedVehicle.plateNumber}
                  </Typography>

                  <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'stretch' : 'center'} sx={{ mb: 1.5 }}>
                    <Autocomplete
                      options={vehicles}
                      getOptionLabel={v => `${v.plateNumber} — ${v.make} ${v.model}`}
                      value={selectedVehicle}
                      onChange={(_, newVal) => { if (newVal) setSelectedVehicle(newVal); }}
                      size="small"
                      sx={{ minWidth: 260 }}
                      renderInput={params => <TextField {...params} label="Select Vehicle" />}
                    />
                    <TextField
                      size="small"
                      placeholder="Search zones..."
                      value={zoneSearch}
                      onChange={e => setZoneSearch(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    />
                  </Stack>
                </Box>

                {/* Geofencing Toggle */}
                <Alert
                  severity={selectedVehicle.geofencingEnabled ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                  icon={selectedVehicle.geofencingEnabled ? <CheckCircleIcon /> : <WarningIcon />}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 1 : 0
                  }}>
                    <Typography variant={isMobile ? 'caption' : 'body2'}>
                      Geofencing is {selectedVehicle.geofencingEnabled ? 'enabled' : 'disabled'}
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedVehicle.geofencingEnabled}
                          onChange={async (e) => {
                            const enabled = e.target.checked;

                            try {
                              await api.put(`/geofencing/vehicles/${selectedVehicle._id}/enable`, { enabled });
                              await fetchVehicles();
                              setSelectedVehicle({
                                ...selectedVehicle,
                                geofencingEnabled: enabled
                              });
                              alert(`Geofencing ${enabled ? 'enabled' : 'disabled'}`);
                            } catch (error: any) {
                              alert(`Failed: ${error.response?.data?.message || error.message}`);
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={selectedVehicle.geofencingEnabled ? 'Enabled' : 'Disabled'}
                    />
                  </Box>
                </Alert>

                {/* Zone List */}
                <List sx={{ p: 0 }}>
                  {zones
                    .filter(z => !zoneSearch || z.name.toLowerCase().includes(zoneSearch.toLowerCase()) || z.type.toLowerCase().includes(zoneSearch.toLowerCase()))
                    .map((zone) => (
                      <ListItem
                        key={zone._id}
                        sx={{
                          border: 1,
                          borderColor: zone.isActive ? 'success.light' : 'divider',
                          borderRadius: 1,
                          mb: 1,
                          p: isMobile ? 1 : 1.5,
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'flex-start' : 'center'
                        }}
                      >
                        <ListItemText
                          primary={
                            <Stack spacing={0.5}>
                              <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="bold">
                                {zone.name}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  label={zone.type}
                                  sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                />
                                {zone.isActive ? (
                                  <Chip
                                    size="small"
                                    label="Active"
                                    color="success"
                                    sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    label="Inactive"
                                    sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                  />
                                )}
                                {zone.activeSchedule?.enabled && (
                                  <Chip
                                    size="small"
                                    label="Scheduled"
                                    color="info"
                                    sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                  />
                                )}
                                {zone.rules.speedLimit && (
                                  <Chip
                                    size="small"
                                    label={`${zone.rules.speedLimit} km/h`}
                                    sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                  />
                                )}
                              </Box>
                            </Stack>
                          }
                        />

                        <Box sx={{
                          display: 'flex',
                          gap: 0.5,
                          mt: isMobile ? 1 : 0,
                          width: isMobile ? '100%' : 'auto',
                          justifyContent: isMobile ? 'flex-end' : 'flex-start'
                        }}>
                          <IconButton
                            size="small"
                            onClick={() => duplicateZone(zone)}
                            title="Duplicate zone"
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingZone(zone);
                              setOpenZoneDialog(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteZone(zone._id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItem>
                    ))}
                  {zones.length === 0 && (
                    <Typography variant="caption" color="textSecondary" align="center" sx={{ display: 'block', py: 4 }}>
                      No zones configured. Click "Add Zone" to create one.
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Violations */}
      {activeTab === 2 && (
        <ViolationsTab isMobile={isMobile} />
      )}

      {/* Tab 3: Analytics */}
      {activeTab === 3 && analytics && (
        <GeofenceAnalyticsPanel analytics={analytics} onRefresh={fetchAnalytics} />
      )}

      {/* Dialogs */}
      <GeofenceZoneDialog
        open={openZoneDialog}
        onClose={() => {
          setOpenZoneDialog(false);
          setEditingZone(null);
          setMultiVehicleMode(false);
        }}
        vehicleId={multiVehicleMode ? undefined : selectedVehicle?._id}
        zoneData={editingZone}
        onSuccess={() => {
          setOpenZoneDialog(false);
          setEditingZone(null);
          setMultiVehicleMode(false);
          if (selectedVehicle) {
            fetchZones(selectedVehicle._id);
          }
          fetchVehicles();
          fetchStatistics();
        }}
      />

      <BulkZoneDialog
        open={openBulkDialog}
        onClose={() => setOpenBulkDialog(false)}
        vehicles={vehicles}
        onSuccess={() => {
          setOpenBulkDialog(false);
          fetchVehicles();
        }}
      />

      <ZoneTemplatesDialog
        open={openTemplatesDialog}
        onClose={() => setOpenTemplatesDialog(false)}
        onSelect={(template) => {
          setOpenTemplatesDialog(false);
          setOpenZoneDialog(true);
        }}
      />

      {/* Export Dialog - Mobile Responsive */}
      <Dialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Export Violations</Typography>
            {isMobile && (
              <IconButton onClick={() => setOpenExportDialog(false)} size="small">
                <DownloadIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose export format:
          </Typography>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                exportViolations('csv');
                setOpenExportDialog(false);
              }}
            >
              CSV Format
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                exportViolations('json');
                setOpenExportDialog(false);
              }}
            >
              JSON Format
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          p: 2,
          '& > button': {
            width: isMobile ? '100%' : 'auto'
          }
        }}>
          <Button onClick={() => setOpenExportDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};



export default GeofencingDashboard;